import { consoleRoutes, WORKFLOWS_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Dialog, DialogContent, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";

const U = "engage.workflows.defaults.upgrade";

/** Where "Your workflows" sits on a plan without them (Starter): the defaults above still work in full. */
export function OwnWorkflowsUpgrade() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <section className="card card-body flex flex-col gap-4 md:flex-row md:items-center" aria-labelledby="own-workflows-upgrade" data-testid="plan-gate">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
        <Icon name="lock" className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id="own-workflows-upgrade" className="text-base font-bold">{t(`${U}.title`)}</h2>
        <p className="mt-1 text-sm text-fg-muted">{t(`${U}.body`)}</p>
      </div>
      <Button asChild>
        <AppLink href={consoleRoutes.billing(summary.slug)}>
          <Icon name="arrow-up-right" />
          {t(`${U}.cta`)}
        </AppLink>
      </Button>
    </section>
  );
}

interface UpgradeSheetProps {
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/** What a plan-locked action (New workflow, Templates, Duplicate) opens on Starter, instead of an error page. */
export function UpgradeSheet({ onClose, onCloseAutoFocus }: UpgradeSheetProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${U}.sheetTitle`)}
        description={t(`${U}.sheetBody`)}
        closeLabel={t("common.close")}
        onCloseAutoFocus={onCloseAutoFocus}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t(`${U}.sheetClose`)}</Button>
            <Button asChild>
              <AppLink href={consoleRoutes.billing(summary.slug)}>
                <Icon name="arrow-up-right" />
                {t(`${U}.cta`)}
              </AppLink>
            </Button>
          </>
        }
      >
        <p className="flex items-center gap-2 text-sm font-semibold">
          <Icon name="lock" className="size-4" />
          {t(`${U}.lock`, { plan: t(`console.plans.${WORKFLOWS_MIN_PLAN}`) })}
        </p>
      </DialogContent>
    </Dialog>
  );
}
